import { supabase } from '../lib/supabase';

export const vetrineService = {
  // Get all vetrine stores (public access) - shows both demo and real stores
  async getAllStores() {
    console.log('🔍 vetrineService.getAllStores: Starting query...');
    
    // Remove status filter to show both demo and real stores
    // The badge in UI will distinguish between demo and real
    const { data, error } = await supabase?.from('vetrine_stores')?.select('*')?.order('created_at', { ascending: false });

    console.log('📊 Supabase response:', { data, error });
    console.log('📊 Data count:', data?.length);
    
    if (error) {
      console.error('❌ Supabase error:', error);
      throw error;
    }

    const converted = data?.map(this.convertToCamelCase) || [];
    console.log('✅ Converted data:', converted);
    
    return converted;
  },

  // Get stores filtered by platform at query level (no duplicates)
  // platform: 'cliccami' | 'unidaymc'
  async getStoresByPlatform(platform) {
    console.log(`🔍 vetrineService.getStoresByPlatform: platform=${platform}`);

    const { data, error } = await supabase
      ?.from('vetrine_stores')
      ?.select('*')
      ?.or(`platform_target.eq.${platform},platform_target.eq.both`)
      ?.order('created_at', { ascending: false });

    console.log('📊 getStoresByPlatform response:', { data, error });

    if (error) {
      console.error('❌ getStoresByPlatform error:', error);
      throw error;
    }

    return data?.map(this.convertToCamelCase) || [];
  },

  // Get featured stores filtered by platform at query level
  // platform: 'cliccami' | 'unidaymc'
  async getFeaturedStoresByPlatform(platform) {
    console.log(`🔍 vetrineService.getFeaturedStoresByPlatform: platform=${platform}`);

    const { data, error } = await supabase
      ?.from('vetrine_stores')
      ?.select('*')
      ?.eq('in_evidenza', true)
      ?.or(`platform_target.eq.${platform},platform_target.eq.both`)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('❌ getFeaturedStoresByPlatform error:', error);
      throw error;
    }

    return data?.map(this.convertToCamelCase) || [];
  },

  // Get featured stores (in_evidenza = true)
  async getFeaturedStores() {
    const { data, error } = await supabase
      ?.from('vetrine_stores')
      ?.select('*')
      ?.eq('in_evidenza', true)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(this.convertToCamelCase) || [];
  },

  // Get single store by ID - SIMPLE query without any embedded relationships
  async getStoreById(storeId) {
    console.log('🔍 getStoreById called with ID:', storeId);
    
    // Simple select with ALL columns, NO embedded relationships
    const { data, error } = await supabase
      ?.from('vetrine_stores')
      ?.select('*')
      ?.eq('id', storeId)
      ?.single();

    console.log('📦 getStoreById result:', { data, error });

    if (error) {
      console.error('❌ getStoreById error:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Vetrina non trovata');
    }

    return this.convertToCamelCase(data);
  },

  // Create new store (admin only)
  async createStore(storeData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Non autenticato');

    const dbData = this.convertToSnakeCase(storeData);
    dbData.created_by_admin = user?.id;
    dbData.updated_by_admin = user?.id;

    const { data, error } = await supabase?.from('vetrine_stores')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  },

  // Update store (admin only) - SIMPLE update without any embedded relationships
  async updateStore(storeId, storeData) {
    console.log('🔄 updateStore called:', { storeId, storeData });
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Non autenticato');

    const dbData = this.convertToSnakeCase(storeData);
    dbData.updated_by_admin = user?.id;

    console.log('💾 Updating with data:', dbData);

    // Simple update query, NO embedded relationships
    const { data, error } = await supabase
      ?.from('vetrine_stores')
      ?.update(dbData)
      ?.eq('id', storeId)
      ?.select('*')
      ?.single();

    console.log('✅ Update result:', { data, error });

    if (error) {
      console.error('❌ Update error:', error);
      throw error;
    }

    return this.convertToCamelCase(data);
  },

  // Delete store (admin only)
  async deleteStore(storeId) {
    const { error } = await supabase?.from('vetrine_stores')?.delete()?.eq('id', storeId);

    if (error) throw error;
  },

  // Get all categories
  async getCategories() {
    const { data, error } = await supabase?.from('vetrine_categories')?.select('*')?.order('label');

    if (error) {
      console.error('❌ Error loading categories:', error);
      throw new Error(`Errore nel caricamento delle categorie: ${error.message}`);
    }

    console.log('✅ Categories loaded successfully:', data?.length, 'items');
    return data || [];
  },

  // Get all zones
  async getZones() {
    const { data, error } = await supabase?.from('vetrine_zones')?.select('*')?.order('label');

    if (error) {
      console.error('❌ Error loading zones:', error);
      throw new Error(`Errore nel caricamento delle zone: ${error.message}`);
    }

    console.log('✅ Zones loaded successfully:', data?.length, 'items');
    return data || [];
  },

  // Upload image to storage
  async uploadImage(file, folder = 'stores') {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${folder}/${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

    const { data, error } = await supabase?.storage
      ?.from('storefront-images')
      ?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    return fileName;
  },

  // Get public URL for image
  getImageUrl(filePath) {
    if (!filePath) return null;
    
    // If already a full URL, return as-is
    if (filePath?.startsWith('http://') || filePath?.startsWith('https://')) {
      return filePath;
    }
    
    const { data } = supabase?.storage
      ?.from('storefront-images')
      ?.getPublicUrl(filePath);

    return data?.publicUrl || null;
  },

  // Delete image from storage
  async deleteImage(filePath) {
    if (!filePath) return;

    const { error } = await supabase?.storage
      ?.from('storefront-images')
      ?.remove([filePath]);

    if (error) throw error;
  },

  // Convert database snake_case to React camelCase
  convertToCamelCase(row) {
    if (!row) return null;

    return {
      id: row?.id,
      userId: row?.user_id,
      nomeNegozio: row?.nome_negozio,
      categoria: row?.categoria,
      zona: row?.zona,
      indirizzo: row?.indirizzo,
      telefono: row?.telefono,
      email: row?.email,
      whatsapp: row?.whatsapp,
      instagram: row?.instagram,
      facebook: row?.facebook,
      sitoWeb: row?.sito_web,
      descrizioneBreve: row?.descrizione_breve,
      orariApertura: row?.orari_apertura,
      rating: row?.rating,
      numeroRecensioni: row?.numero_recensioni,
      inEvidenza: row?.in_evidenza,
      cashback: row?.cashback,
      status: row?.status,
      promoAssociata: row?.promo_associata,
      tipoVetrina: row?.tipo_vetrina,
      platformTarget: row?.platform_target,
      servizi: {
        consegna: row?.servizi_consegna,
        prenotazione: row?.servizi_prenotazione,
        appuntamento: row?.servizi_appuntamento,
        pagamentiDigitali: row?.servizi_pagamenti_digitali,
        convenzioni: row?.servizi_convenzioni
      },
      prodotto1: row?.prodotto1,
      prodotto2: row?.prodotto2,
      prodotto3: row?.prodotto3,
      promoTitolo: row?.promo_titolo,
      promoDescrizione: row?.promo_descrizione,
      promoScadenza: row?.promo_scadenza,
      logoNegozio: row?.logo_negozio,
      immaginePrincipale: row?.immagine_principale,
      galleryProdotti: row?.gallery_prodotti,
      createdByAdmin: row?.created_by_admin,
      updatedByAdmin: row?.updated_by_admin,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  },

  // Convert React camelCase to database snake_case
  convertToSnakeCase(data) {
    const snakeData = {};

    if (data?.nomeNegozio !== undefined) snakeData.nome_negozio = data?.nomeNegozio;
    if (data?.categoria !== undefined) snakeData.categoria = data?.categoria;
    if (data?.zona !== undefined) snakeData.zona = data?.zona;
    if (data?.indirizzo !== undefined) snakeData.indirizzo = data?.indirizzo;
    if (data?.telefono !== undefined) snakeData.telefono = data?.telefono;
    if (data?.email !== undefined) snakeData.email = data?.email;
    if (data?.whatsapp !== undefined) snakeData.whatsapp = data?.whatsapp;
    if (data?.instagram !== undefined) snakeData.instagram = data?.instagram;
    if (data?.facebook !== undefined) snakeData.facebook = data?.facebook;
    if (data?.sitoWeb !== undefined) snakeData.sito_web = data?.sitoWeb;
    if (data?.descrizioneBreve !== undefined) snakeData.descrizione_breve = data?.descrizioneBreve;
    if (data?.orariApertura !== undefined) snakeData.orari_apertura = data?.orariApertura;
    if (data?.rating !== undefined) snakeData.rating = data?.rating;
    if (data?.numeroRecensioni !== undefined) snakeData.numero_recensioni = data?.numeroRecensioni;
    if (data?.inEvidenza !== undefined) snakeData.in_evidenza = data?.inEvidenza;
    if (data?.cashback !== undefined) snakeData.cashback = data?.cashback;
    if (data?.status !== undefined) snakeData.status = data?.status;
    if (data?.promoAssociata !== undefined) snakeData.promo_associata = data?.promoAssociata;
    if (data?.tipoVetrina !== undefined) snakeData.tipo_vetrina = data?.tipoVetrina;
    if (data?.platformTarget !== undefined) snakeData.platform_target = data?.platformTarget;
    if (data?.platform_target !== undefined) snakeData.platform_target = data?.platform_target;
    
    if (data?.servizi) {
      if (data?.servizi?.consegna !== undefined) snakeData.servizi_consegna = data?.servizi?.consegna;
      if (data?.servizi?.prenotazione !== undefined) snakeData.servizi_prenotazione = data?.servizi?.prenotazione;
      if (data?.servizi?.appuntamento !== undefined) snakeData.servizi_appuntamento = data?.servizi?.appuntamento;
      if (data?.servizi?.pagamentiDigitali !== undefined) snakeData.servizi_pagamenti_digitali = data?.servizi?.pagamentiDigitali;
      if (data?.servizi?.convenzioni !== undefined) snakeData.servizi_convenzioni = data?.servizi?.convenzioni;
    }

    if (data?.prodotto1 !== undefined) snakeData.prodotto1 = data?.prodotto1;
    if (data?.prodotto2 !== undefined) snakeData.prodotto2 = data?.prodotto2;
    if (data?.prodotto3 !== undefined) snakeData.prodotto3 = data?.prodotto3;
    if (data?.promoTitolo !== undefined) snakeData.promo_titolo = data?.promoTitolo;
    if (data?.promoDescrizione !== undefined) snakeData.promo_descrizione = data?.promoDescrizione;
    if (data?.promoScadenza !== undefined) snakeData.promo_scadenza = data?.promoScadenza;
    if (data?.logoNegozio !== undefined) snakeData.logo_negozio = data?.logoNegozio;
    if (data?.immaginePrincipale !== undefined) snakeData.immagine_principale = data?.immaginePrincipale;
    if (data?.galleryProdotti !== undefined) snakeData.gallery_prodotti = data?.galleryProdotti;

    return snakeData;
  }
};